import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Layout, SECTIONS, pluralWord, liveCount } from '@alatzidis/core'
import { ARTIFACTS_BY_SECTION } from '../artifacts'

/**
 * Counted, not typed out.
 *
 * This was a hand-written `COUNTS` table until 2026-08-30, and by then it said
 * `02` for chess and `05` for sound while both sections held three pages — the
 * front page of the site, wrong about two of its four sections, for two weeks.
 * The table was correct on the day it was written and silently stopped being so
 * every time an artifact was added or a route retired.
 *
 * Zero-padded to two digits because the number sits in a mono row beside the
 * section's own `01`..`04` and a bare `5` breaks the column.
 */
const count = (id: (typeof SECTIONS)[number]['id']) =>
  liveCount(ARTIFACTS_BY_SECTION[id])

export function Home() {
  return (
    <Layout section="home">
      <section className="mx-auto max-w-6xl px-6 md:px-10">
        {/* Hero — type as art. */}
        <div className="grid grid-cols-12 gap-4 pb-32 pt-12">
          <div className="col-span-12 md:col-span-2">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[color:var(--color-muted)]">
              00—index
            </p>
          </div>

          <div className="col-span-12 md:col-span-10">
            {/* The wordmark is the name and the mark together — `alatzidis /
                ∫ play`. The integral stays the largest thing on the page; the
                name and `play` share a size so the slash reads as a join
                rather than a demotion. */}
            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-wrap items-baseline gap-x-4 gap-y-2 font-[var(--font-display)] font-medium leading-none tracking-tight md:gap-x-6"
            >
              <span className="text-[clamp(2.75rem,9vw,7rem)] leading-none">
                alatzidis
              </span>
              <span
                aria-hidden
                className="text-[clamp(2rem,6vw,4.5rem)] leading-none text-[color:var(--color-fg)]/25"
              >
                /
              </span>
              <span className="text-[clamp(5rem,18vw,15rem)] leading-none">
                &int;
              </span>
              <span className="italic text-[clamp(2.75rem,9vw,7rem)] leading-none text-[color:var(--color-fg)]/70">
                play
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="mt-16 font-mono text-sm uppercase tracking-[0.2em] text-[color:var(--color-fg)]/80"
            >
              Nothing here was downloaded.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 max-w-xl text-base leading-relaxed text-[color:var(--color-fg)]/70"
            >
              <strong className="font-medium text-[color:var(--color-fg)]">
                First principles, played with.
              </strong>{' '}
              Sound from oscillators, games from rules, chess from stubbornness.
              Small machines, taken apart on purpose — by a family that builds
              its own toys.
            </motion.p>
          </div>
        </div>

        {/* Sections. Each tile colors itself on hover with its accent — the
            "playful" moment in an otherwise quiet page. */}
        <div className="grid grid-cols-1 gap-px bg-[color:var(--color-line)] md:grid-cols-2">
          {SECTIONS.map((s, i) => (
            <motion.div
              key={s.id}
              // An odd section out takes the full row rather than leaving a
              // hole in the two-column grid.
              className={
                SECTIONS.length % 2 === 1 && i === SECTIONS.length - 1
                  ? 'md:col-span-2'
                  : undefined
              }
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
            >
              <Link
                to={s.href}
                data-cursor="hover"
                data-section={s.id}
                className="group relative block bg-[color:var(--color-bg)] p-8 transition-colors hover:bg-[color:var(--color-surface)] md:p-12"
              >
                <div className="flex items-baseline justify-between font-mono text-xs uppercase tracking-[0.2em] text-[color:var(--color-muted)]">
                  <span style={{ color: 'var(--accent)' }}>{s.number}</span>
                  <span>
                    {String(count(s.id)).padStart(2, '0')}{' '}
                    {pluralWord(count(s.id), 'item')}
                  </span>
                </div>

                <h2 className="mt-12 font-[var(--font-display)] text-5xl font-medium leading-none tracking-tight transition-colors group-hover:text-[color:var(--accent)] md:text-7xl">
                  {s.label}
                </h2>

                <p className="mt-6 max-w-sm text-sm leading-relaxed text-[color:var(--color-fg)]/60">
                  {s.blurb}
                </p>

                <div
                  aria-hidden
                  className="mt-8 h-px w-0 transition-all duration-500 group-hover:w-16"
                  style={{ background: 'var(--accent)' }}
                />
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Now line — small, monospaced, signs off the page. */}
        <div className="mt-32 grid grid-cols-12 gap-4 font-mono text-xs uppercase tracking-[0.2em] text-[color:var(--color-muted)]">
          <div className="col-span-12 md:col-span-2">now</div>
          <div className="col-span-12 md:col-span-10">
            <span className="text-[color:var(--color-fg)]">Building instruments.</span>{' '}
            Five of them, on one clock. Notes are next.
          </div>
        </div>
      </section>
    </Layout>
  )
}
